import styled from '@emotion/styled'
import { useEffect, useRef, useState } from 'react'
import { FlexColumn } from 'src/components/flex-containers'
import { Colors, Shapes } from 'src/components/theme'
import { rgbaStrOpacity, rgbaToRgb } from 'src/utils/colors'
import { IconBox } from 'src/v2/components/icon-box'

const Wrapper = styled(FlexColumn)`
  position: relative;
  gap: ${Shapes.spacing[5]}px;
`

const Row = styled.div`
  z-index: 1;
  display: flex;
  flex-direction: row;
  gap: ${Shapes.spacing[4]}px;
  align-items: center;
`
const IconColumn = styled.div`
  flex-shrink: 0;
`
const ContentColumn = styled.div`
  flex: 1;
  overflow: hidden;
  * {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`
const ConnectorLine = styled.div`
  position: absolute;
  width: 1px;
  background: ${(props) => props.color};
  transform: translate(-50%);
`

type ChronologicalListItem = {
  icon: React.ReactNode
  content: React.ReactNode
}
type ChronologicalList = {
  color?: string
  items: ChronologicalListItem[]
  iconSize?: number
}
export const ChronologicalList = (props: ChronologicalList) => {
  const { iconSize, items, color } = props
  const wrapperRef = useRef<HTMLDivElement>(null)
  const firstIconRef = useRef<HTMLDivElement>(null)
  const lastIconRef = useRef<HTMLDivElement>(null)
  const [offsets, setOffsets] = useState({
    wrapperTop: 0,
    firstIconCenter: 0,
    lastIconCenter: 0,
  })

  useEffect(() => {
    const calculateOffsets = () => {
      if (wrapperRef.current && firstIconRef.current && lastIconRef.current) {
        const wrapperTop = wrapperRef.current.getBoundingClientRect().top
        const firstIconRect = firstIconRef.current.getBoundingClientRect()
        const lastIconRect = lastIconRef.current.getBoundingClientRect()

        const firstIconCenter = firstIconRect.top + firstIconRect.height / 2
        const lastIconCenter = lastIconRect.top + lastIconRect.height / 2

        setOffsets({
          wrapperTop,
          firstIconCenter,
          lastIconCenter,
        })
      }
    }

    // Calculate offsets initially
    calculateOffsets()
    // Recalculate offsets on window resize
    window.addEventListener('resize', calculateOffsets)
    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', calculateOffsets)
  }, [])

  return (
    <Wrapper ref={wrapperRef}>
      {items.map((item, index) => (
        <Row key={index + `chronolist`}>
          <IconColumn ref={index === 0 ? firstIconRef : index === items.length - 1 ? lastIconRef : null}>
            <IconBox
              size={iconSize || Shapes.spacing[7]}
              color={color || Colors.neutral700}
              secondaryColor={rgbaToRgb(rgbaStrOpacity(color || Colors.neutral700, 0.1))}>
              {item.icon}
            </IconBox>
          </IconColumn>
          <ContentColumn>{item.content}</ContentColumn>
        </Row>
      ))}
      <ConnectorLine
        style={{
          // Start at the middle of the first icon
          top: `${offsets.firstIconCenter - offsets.wrapperTop}px`,
          left: `${iconSize || Shapes.spacing[7] / 2}px`,
          // Span the distance between the middle of the first and last icons
          height: `${offsets.lastIconCenter - offsets.firstIconCenter}px`,
        }}
        color={rgbaToRgb(rgbaStrOpacity(color || Colors.neutral700, 0.15))}
      />
    </Wrapper>
  )
}
